import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../Reducer/loaderSlice";
import { setUnreadCounts } from "../Reducer/chatSlice";
import Handler from "../Helpers/Handler";
import logo from "../Assets/images/q-nexus.png";
import { Button } from "reactstrap";
import { useAuth } from "../Context/AuthenticateProvider";

const ADMIN_ID = 1;

/* ── Sub-menu items for Events ── */
const EVENT_LINKS = [
  { to: "/user/events", icon: "bx-list-ul", label: "All Events" },
  { to: "/user/create-event", icon: "bx-plus-circle", label: "Create Event" },
];

/* ── Sub-menu items for Community ── */
const COMMUNITY_LINKS = [
  { to: "/user/communities", icon: "bx-group", label: "My Communities" },
  { to: "/user/create-community", icon: "bx-plus-circle", label: "Create Community" },
];

/* ── Sub-menu items for Meetings ── */
const MEETING_LINKS = [
  { to: "/user/meetings", icon: "bx-list-ul", label: "All Meetings" },
  { to: "/user/create-meeting", icon: "bx-calendar-plus", label: "New Meeting" },
  { to: "/user/meeting-calendar", icon: "bx-calendar", label: "Calendar View" },
];

const LeftSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { logOut } = useAuth();

  const unreadTotal = useSelector((state) => state.chat?.unreadTotal);

  /* ── Active path checks ── */
  const isEventPath =
    location.pathname.startsWith("/user/event") ||
    location.pathname.startsWith("/user/create-event") ||
    location.pathname.startsWith("/user/link-community") ||
    location.pathname.startsWith("/user/invite");

  const isCommunityPath =
    location.pathname.startsWith("/user/communit") ||
    location.pathname.startsWith("/user/create-community");

  const isMeetingPath =
    location.pathname.startsWith("/user/meeting") ||
    location.pathname.startsWith("/user/create-meeting") ||
    location.pathname.startsWith("/user/edit-meeting");

  /* ── Accordion open states ── */
  const [eventsOpen, setEventsOpen] = useState(isEventPath);
  const [communityOpen, setCommunityOpen] = useState(isCommunityPath);
  const [meetingsOpen, setMeetingsOpen] = useState(isMeetingPath);

  /* Keep accordions in sync on browser back/forward navigation */
  useEffect(() => {
    if (isEventPath) setEventsOpen(true);
    if (isCommunityPath) setCommunityOpen(true);
    if (isMeetingPath) setMeetingsOpen(true);
  }, [location.pathname]);

  /* ── Unread count polling ── */
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const r = await Handler({ method: "get", url: "/chat/unread_count.php" });
        if (r?.success) {
          let total = 0;
          (r.data || []).forEach((i) => (total += i.unread_count));
          dispatch(setUnreadCounts(total));
        }
      } catch { }
    };
    fetchUnread();
    window.addEventListener("focus", fetchUnread);
    return () => window.removeEventListener("focus", fetchUnread);
  }, [dispatch]);

  /* ── Start admin chat ── */
  const startAdminChat = async () => {
    dispatch(loadingTrue());
    try {
      const r = await Handler({
        method: "post",
        url: "/admin_chat/start.php",
        data: { user_id: ADMIN_ID },
      });
      if (r?.success) {
        navigate("/user/admin-chat");
        onClose?.();
      }
    } catch (e) {
      console.error(e);
    }
    dispatch(loadingFalse());
  };

  /* Close sidebar on mobile after any nav */
  const handleNav = () => onClose?.();

  return (
    <>
      {isOpen && (
        <div className="sidebar-overlay d-lg-none" onClick={onClose} />
      )}

      <aside className={`qnexus-sidebar ${isOpen ? "open" : ""}`}>

        {/* Brand */}
        <div className="sidebar-brand">
          <img src={logo} alt="Qnexus" />
        </div>

        {/* Main nav */}
        <nav className="sidebar-menu">

          {/* Home */}
          <NavLink to="/user/feed" end onClick={handleNav}>
            <i className="bx bx-home-alt me-2" />
            Home
          </NavLink>

          {/* Posts */}
          <NavLink to="/user/create-post" onClick={handleNav}>
            <i className="bx bx-plus-circle me-2" />
            Create Post
          </NavLink>

          <NavLink to="/user/my-posts" onClick={handleNav}>
            <i className="bx bx-grid-alt me-2" />
            My Posts
          </NavLink>

          {/* Messages */}
          <NavLink to="/user/chat-list" style={{ position: "relative" }} onClick={handleNav}>
            <i className="bx bx-message-square-dots me-2" />
            Messages
            {unreadTotal > 0 && (
              <span className="sidebar-badge">{unreadTotal}</span>
            )}
          </NavLink>

          {/* ── Community accordion ── */}
          <button
            className={`sidebar-accordion ${communityOpen ? "accordion-open" : ""} ${isCommunityPath ? "accordion-active" : ""}`}
            onClick={() => setCommunityOpen((o) => !o)}
            type="button"
          >
            <span>
              <i className="bx bx-group me-2" />
              Community
            </span>
            <i className={`bx bx-chevron-down accordion-arrow ${communityOpen ? "rotated" : ""}`} />
          </button>

          {communityOpen && (
            <div className="sidebar-submenu">
              {COMMUNITY_LINKS.map(({ to, icon, label }) => (
                <NavLink key={to} to={to} className="sidebar-sublink" onClick={handleNav}>
                  <i className={`bx ${icon} me-2`} />
                  {label}
                </NavLink>
              ))}
            </div>
          )}

          {/* ── Events accordion ── */}
          <button
            className={`sidebar-accordion ${eventsOpen ? "accordion-open" : ""} ${isEventPath ? "accordion-active" : ""}`}
            onClick={() => setEventsOpen((o) => !o)}
            type="button"
          >
            <span>
              <i className="bx bx-calendar-event me-2" />
              Events
            </span>
            <i className={`bx bx-chevron-down accordion-arrow ${eventsOpen ? "rotated" : ""}`} />
          </button>

          {eventsOpen && (
            <div className="sidebar-submenu">
              {EVENT_LINKS.map(({ to, icon, label }) => (
                <NavLink key={to} to={to} className="sidebar-sublink" onClick={handleNav}>
                  <i className={`bx ${icon} me-2`} />
                  {label}
                </NavLink>
              ))}
            </div>
          )}

          {/* ── Meetings accordion ── */}
          <button
            className={`sidebar-accordion ${meetingsOpen ? "accordion-open" : ""} ${isMeetingPath ? "accordion-active" : ""}`}
            onClick={() => setMeetingsOpen((o) => !o)}
            type="button"
          >
            <span>
              <i className="bx bx-video me-2" />
              Meetings
            </span>
            <i className={`bx bx-chevron-down accordion-arrow ${meetingsOpen ? "rotated" : ""}`} />
          </button>

          {meetingsOpen && (
            <div className="sidebar-submenu">
              {MEETING_LINKS.map(({ to, icon, label }) => (
                <NavLink key={to} to={to} className="sidebar-sublink" onClick={handleNav}>
                  <i className={`bx ${icon} me-2`} />
                  {label}
                </NavLink>
              ))}
            </div>
          )}

          {/* Mini CRM */}
          <NavLink to="/user/crm" onClick={handleNav}>
            <i className="bx bx-line-chart me-2" />
            Mini CRM
          </NavLink>

          {/* Vendor Reviews */}
          <NavLink to="/user/reviews" onClick={handleNav}>
            <i className="bx bx-star me-2" />
            Vendor Reviews
          </NavLink>

          {/* Top Vendors */}
          <NavLink to="/user/top-vendors" onClick={handleNav}>
            <i className="bx bx-medal me-2" />
            Top Vendors
          </NavLink>

          {/* Get Verified */}
          <NavLink to="/user/verification" onClick={handleNav}>
            <i className="bx bx-badge-check me-2" />
            Get Verified
          </NavLink>

        </nav>

        {/* Help card */}
        <div className="sidebar-help">
          <h6>Need Assistance?</h6>
          <p>Chat with an Admin for support and enquiries</p>
          <button onClick={startAdminChat}>Chat Now</button>
        </div>

        <Button color="danger" onClick={logOut}>Logout</Button>
      </aside>
    </>
  );
};

export default LeftSidebar;